import { Link, usePage } from "@inertiajs/react"
import { Sidebar } from "primereact/sidebar"
import { Button } from "primereact/button"
import { useEffect, useState, useRef } from "react"
import { RxHamburgerMenu } from "react-icons/rx"
import { SearchBarV2 } from "@/Components/SearchBarV2.jsx"
import { ChevronDown } from "lucide-react"

const navLinks = [
    { href: "/auctions", label: "Auctions" },
    { href: "/brands", label: "Brands" },
    { href: "/categories", label: "Categories" },
    { href: "/about-us", label: "About Us" },
    {
        label: "Sell Your Gun",
        children: [
            { href: "/sell-my-gun", label: "Cash Offer" },
            { href: "/gun-consignment", label: "Consignment" },
        ],
    },
]

export default function Header({ children, className = "", ...props }) {
    const [visibleRight, setVisibleRight] = useState(false)
    const [openDropdown, setOpenDropdown] = useState(null)
    const [showSearch, toggleSearch] = useState(false)
    const dropdownRef = useRef(null)
    const leaveTimeout = useRef(null)

    useEffect(() => {
        if (visibleRight) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "auto"
        }
    }, [visibleRight])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setOpenDropdown(null)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [dropdownRef])

    useEffect(() => {
        return () => clearTimeout(leaveTimeout.current)
    }, [dropdownRef])

    return (
        <header
            className={`sticky top-0 bg-white z-[99]`}
            style={{
                boxShadow:
                    "0px 1px 1px 0px rgba(0, 0, 0, 0.06), 0px 1px 2px 0px rgba(0, 0, 0, 0.10)",
            }}
        >
            <div
                {...props}
                className={
                    `container flex justify-between mx-auto py-5 px-5 xl:px-10 2xl:px-0 ${
                        showSearch ? "mb-[75px]" : ""
                    }` + className
                }
            >
                <div className="flex items-center">
                    <div className="inline-block me-8">
                        <Link href="/">
                            <img
                                src="/images/logo.webp"
                                className=" max-w-[148px]"
                                alt="FirearmLand Logo"
                                fetchPriority="high"
                                loading="eager"
                            />
                        </Link>
                    </div>

                    <div className="items-center hidden desktop:flex gap-x-8">
                        {navLinks.map(
                            (link) =>
                                !link?.children && (
                                    <Link
                                        key={link.href}
                                        prefetch
                                        href={link.href}
                                    >
                                        {link.label}
                                    </Link>
                                )
                        )}
                    </div>
                </div>

                <div className="flex flex-row gap-3">
                    <SearchBarV2
                        showSearchBar={showSearch}
                        toggleSearch={toggleSearch}
                    />

                    <Button
                        aria-label="Open menu"
                        className="flex justify-center min-w-8 desktop:hidden"
                        onClick={() => setVisibleRight(true)}
                    >
                        <RxHamburgerMenu width={24} height={24} />
                    </Button>

                    <Link
                        href="/sell-my-gun"
                        className="hidden desktop:flex items-center justify-center px-[20px] py-2 text-neutral-50 rounded-lg transition-colors bg-[#800020] hover:bg-maroon-light w-[160px] text-center"
                    >
                        Sell a Gun
                    </Link>
                </div>
            </div>

            <Sidebar
                className="bg-[#000]"
                visible={visibleRight}
                position="right"
                onHide={() => setVisibleRight(false)}
            >
                <div className="flex flex-col text-white gap-y-8">
                    <div className="inline-block me-8">
                        <img
                            src="/images/logo.webp"
                            className=" max-w-[148px]"
                            style={{ position: "absolute", top: "28px" }}
                            alt="FirearmLand Logo"
                        />
                    </div>

                    <h4 className="text-2xl font-semibold">Menu</h4>
                    {navLinks.map((link) =>
                        link?.children ? (
                            <div
                                key={link.label}
                                className="relative"
                                ref={
                                    openDropdown === link.label
                                        ? dropdownRef
                                        : null
                                }
                                onMouseLeave={() => {
                                    leaveTimeout.current = setTimeout(() => {
                                        setOpenDropdown(null)
                                    }, 200)
                                }}
                                onMouseEnter={() =>
                                    clearTimeout(leaveTimeout.current)
                                }
                            >
                                <button
                                    onClick={() =>
                                        setOpenDropdown(
                                            openDropdown === link.label
                                                ? null
                                                : link.label
                                        )
                                    }
                                    onMouseEnter={() =>
                                        setOpenDropdown(link.label)
                                    }
                                    className="flex items-center gap-1 outline-none"
                                >
                                    {link.label}
                                    <ChevronDown
                                        size={16}
                                        className={`transition-all duration-300 ease-in-out ${
                                            openDropdown === link.label
                                                ? "-rotate-180"
                                                : "rotate-0"
                                        }`}
                                    />
                                </button>
                                <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                        openDropdown === link.label
                                            ? "max-h-40"
                                            : "max-h-0"
                                    }`}
                                >
                                    <div className="pt-4 text-white">
                                        {link.children.map((child) => (
                                            <Link
                                                key={child.href}
                                                href={child.href}
                                                className="block px-4 py-4 hover:text-white"
                                                onClick={() =>
                                                    setOpenDropdown(null)
                                                }
                                            >
                                                {child.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <Link key={link.href} prefetch href={link.href}>
                                {link.label}
                            </Link>
                        )
                    )}

                    {/* <Link
                        href="/form-submission"
                        className="flex px-[20px] py-[15px] text-neutral-50 rounded-lg transition-colors bg-maroon hover:bg-maroon-light w-[160px] items-center justify-center"
                    >
                        Sell a Gun
                    </Link> */}
                </div>
            </Sidebar>
        </header>
    )
}
